import { useEffect, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { SearchableSelect } from "~/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { toast } from "~/hooks/use-toast";
import {
  INV_CATEGORY_OPTIONS,
  InvProductCategory,
  inventoryProductService,
} from "~/services/inventory/productService";

interface NewProductDialogProps {
  onSuccess?: () => void;
}

type SourceKind = "finished_good" | "raw_material" | "oil";

const KIND_OPTIONS: { value: SourceKind; label: string }[] = [
  { value: "finished_good", label: "Producto Terminado" },
  { value: "raw_material", label: "Materia Prima" },
  { value: "oil", label: "Aceite" },
];

const TRACKING_OPTIONS = [
  { value: "none", label: "Ninguno" },
  { value: "lot", label: "Lote" },
  { value: "serial", label: "Serie" },
];

export function NewProductDialog({ onSuccess }: NewProductDialogProps) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [baseId, setBaseId] = useState("");
  const [kind, setKind] = useState<SourceKind>("finished_good");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [category, setCategory] = useState<InvProductCategory>("producto_terminado");
  const [uom, setUom] = useState("caja");
  const [tracking, setTracking] = useState("lot");
  const [cost, setCost] = useState("");
  const [density, setDensity] = useState("");
  const [companyId, setCompanyId] = useState("");

  const { data: products = [] } = useQuery({
    queryKey: ["inv-product-master-all"],
    queryFn: () => inventoryProductService.getMaster({}),
    enabled: open,
  });

  const { data: companies = [] } = useQuery({
    queryKey: ["inv-companies"],
    queryFn: () => inventoryProductService.getCompanies(),
    enabled: open,
  });

  // Empresa de pruebas por defecto: los asientos contables no deben tocar la empresa real.
  const defaultCompanyId = useMemo(
    () => companies.find((c) => /pruebas/i.test(c.name))?.id ?? "",
    [companies]
  );

  useEffect(() => {
    if (!companyId && defaultCompanyId) setCompanyId(defaultCompanyId);
  }, [companyId, defaultCompanyId]);

  const options = useMemo(
    () =>
      products.map((p) => ({
        value: p.id,
        label: p.name,
        description: p.code ?? undefined,
        searchTerms: `${p.code ?? ""} ${p.name}`,
      })),
    [products]
  );

  const reset = () => {
    setBaseId("");
    setKind("finished_good");
    setCode("");
    setName("");
    setCategory("producto_terminado");
    setUom("caja");
    setTracking("lot");
    setCost("");
    setDensity("");
    setCompanyId("");
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) reset();
  };

  // Prefill from an existing product ("similar a los existentes")
  const applyBase = (id: string) => {
    setBaseId(id);
    const base = products.find((p) => p.id === id);
    if (!base) return;
    setKind(base.source_kind);
    if (base.category) setCategory(base.category);
    setUom(base.stock_uom);
    setTracking(base.tracking || "none");
    setCost(base.standard_cost != null ? String(base.standard_cost) : "");
    setDensity(base.density_kg_per_liter != null ? String(base.density_kg_per_liter) : "");
    setName(`${base.name} (PRUEBA)`);
  };

  const submit = async () => {
    if (!name.trim()) {
      toast({ title: "El nombre es obligatorio", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      const newId = await inventoryProductService.create({
        source_kind: kind,
        code,
        name,
        category,
        stock_uom: uom,
        tracking,
        standard_cost: cost ? Number(cost) : null,
        density_kg_per_liter: kind === "oil" && density ? Number(density) : null,
      });
      const targetCompany = companyId || defaultCompanyId;
      if (newId && targetCompany) {
        await inventoryProductService.setCompany(newId, targetCompany);
      }
      toast({ title: "Producto creado", description: name.trim() });
      handleOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast({
        title: "No se pudo crear el producto",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="h-9">
          <Plus className="mr-1.5 h-4 w-4" />
          Nuevo producto
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nuevo producto (SKU de prueba)</DialogTitle>
          <DialogDescription>
            Crea un producto para probar operaciones. Puedes basarlo en uno existente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Basar en producto existente (opcional)</Label>
            <SearchableSelect
              value={baseId}
              onValueChange={applyBase}
              options={options}
              placeholder="Buscar producto para copiar…"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={kind} onValueChange={(v) => setKind(v as SourceKind)}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KIND_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Categoría</Label>
              <Select value={category} onValueChange={(v) => setCategory(v as InvProductCategory)}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INV_CATEGORY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Código</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} className="h-9" />
            </div>
            <div className="space-y-1.5">
              <Label>Unidad</Label>
              <Input value={uom} onChange={(e) => setUom(e.target.value)} className="h-9" />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Nombre / Descripción</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9" />
          </div>

          <div className="space-y-1.5">
            <Label>Empresa</Label>
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Seleccionar empresa…" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Los SKU de prueba usan la empresa de pruebas para no afectar la contabilidad real.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Seguimiento</Label>
              <Select value={tracking} onValueChange={setTracking}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRACKING_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Costo unitario (CLP)</Label>
              <Input
                type="number"
                min="0"
                step="any"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                className="h-9"
                placeholder="Opcional"
              />
            </div>
          </div>

          {kind === "oil" && (
            <div className="space-y-1.5">
              <Label>Densidad (kg/L)</Label>
              <Input
                type="number"
                min="0"
                step="any"
                value={density}
                onChange={(e) => setDensity(e.target.value)}
                className="h-9"
                placeholder="Ej. 0,915"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" className="h-9" onClick={() => handleOpenChange(false)}>
            Cancelar
          </Button>
          <Button className="h-9" onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Crear
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
